"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BookmarkService = void 0;
const service_1 = require("./service");
const bookmark_1 = require("../models/bookmark");
class BookmarkService extends service_1.Service {
    /**
     * Get all bookmarks.
     * Uses `mb` with version 0 to fetch full list.
     * @param includeDeleted If true, includes items marked as deleted.
     */
    async getBookmarks(includeDeleted = false) {
        // [requestId, "mb", version=0, changes=[]]
        const response = await this.transport.send('mb', [0, []]);
        // console.log('[BookmarkService] Raw response data:', response.data);
        let payload = [];
        if (response.data.length === 1 && Array.isArray(response.data[0])) {
            payload = response.data[0];
        }
        else if (response.data.length >= 3) {
            payload = response.data;
        }
        const newVersion = payload[0] || 0;
        const updatedList = payload[2] || [];
        const items = updatedList.map(item => {
            // item: [ID, TS, Del, Flg, Par, Ord, Title, URL]
            const flags = item[3] || 0;
            return {
                id: item[0],
                creationDate: item[1],
                isDeleted: item[2] === 1,
                flags,
                isFolder: (flags & bookmark_1.BookmarkFlags.FOLDER) !== 0,
                isExpanded: (flags & bookmark_1.BookmarkFlags.EXPANDED) !== 0,
                parentId: item[4],
                sortOrder: item[5],
                title: item[6],
                url: item[7],
            };
        });
        const filteredItems = includeDeleted ? items : items.filter(i => !i.isDeleted);
        return {
            version: newVersion,
            items: filteredItems
        };
    }
    /**
     * Add a bookmark.
     * Uses `mb` sync: `[version, [item]]` over win1251 JSON (WebSocketTransport.encodePdaJson).
     * Wire-verified 2026-06-22: кириллица в title сохраняется корректно; отдельный `mb,1` binary не требуется.
     * @param title Title of the bookmark.
     * @param url URL of the bookmark (e.g. "forum/index.php?showtopic=...").
     * @param parentId Parent Folder ID (0 for root).
     * @returns The temporary ID used. Note: Server assigns a new ID which will be present in the next sync.
     */
    async addBookmark(title, url, parentId = 0) {
        const tempId = Math.floor(Math.random() * 1000000) + 1000000; // Random Temp ID
        const ts = Math.floor(Date.now() / 1000);
        // Item: [ID, TS, Del, Flg, Par, Ord, Title, URL]
        const item = [
            tempId,
            ts,
            0, // Del=0
            0, // Flg=0
            parentId,
            0, // Ord=0
            title,
            url
        ];
        // Send Sync request: [version=0, [item]]
        await this.transport.send('mb', [0, [item]]);
        return tempId;
    }
    /**
     * Add a Folder.
     * @param title Folder name.
     * @param parentId Parent Folder ID (0 for root).
     */
    async addFolder(title, parentId = 0) {
        const tempId = Math.floor(Math.random() * 1000000) + 1000000;
        const ts = Math.floor(Date.now() / 1000);
        // Folder: Flg=1, URL="folder"
        const item = [
            tempId,
            ts,
            0, // Del=0
            1, // Flg=1
            parentId,
            0,
            title,
            'folder'
        ];
        await this.transport.send('mb', [0, [item]]);
        return tempId;
    }
    /**
     * Delete a bookmark.
     * @param id Bookmark ID to delete.
     */
    async deleteBookmark(id) {
        const ts = Math.floor(Date.now() / 1000);
        // To delete, we must send the item with Del=1.
        // We technically need the original Title/URL for consistency, 
        // but typically ID and Del=1 is enough for the server to mark it.
        // However, looking at the protocol, the generic item struct is expected.
        // We will try sending just ID/Del if possible, but safely we should arguably fetch it first.
        // For efficiency, we assume specific fields aren't validated on delete if ID matches.
        // But to be safe, we'll just send empty Title/URL if we don't look it up, 
        // OR we should look it up.
        // Let's look it up quickly to be safe.
        // Optimization: Fetch only if we don't have it cached. (Here we just fetch).
        // Actually, for this implementation, let's just send placeholders.
        // If it fails, we know why. But based on typical sync, ID is key.
        const item = [
            id,
            ts,
            1, // Del=1
            0,
            0,
            0,
            'deleted', // Placeholder
            'deleted' // Placeholder
        ];
        await this.transport.send('mb', [0, [item]]);
    }
    buildWireUpdate(item, fields) {
        let flags = fields.flags ?? item.flags;
        if (fields.expanded !== undefined) {
            flags = fields.expanded
                ? (flags | bookmark_1.BookmarkFlags.EXPANDED)
                : (flags & ~bookmark_1.BookmarkFlags.EXPANDED);
        }
        const ts = Math.floor(Date.now() / 1000);
        return [
            item.id,
            ts,
            0,
            flags,
            fields.parentId !== undefined ? fields.parentId : item.parentId,
            fields.sortOrder !== undefined ? fields.sortOrder : item.sortOrder,
            fields.title !== undefined ? fields.title : item.title,
            fields.url !== undefined ? fields.url : item.url,
        ];
    }
    /**
     * Синхронизировать изменение без предварительного `getBookmarks` (для UI, где список уже в памяти).
     */
    async syncBookmarkUpdate(version, item, fields) {
        const updateItem = this.buildWireUpdate(item, fields);
        await this.transport.send('mb', [version, [updateItem]]);
    }
    async updateBookmark(id, fields) {
        const result = await this.getBookmarks(true);
        const item = result.items.find(i => i.id === id);
        if (!item) {
            throw new Error(`Bookmark ${id} not found`);
        }
        await this.syncBookmarkUpdate(result.version, item, fields);
    }
    /** Раскрыть/свернуть папку (бит EXPANDED). При `context` — без лишнего getBookmarks. */
    async setFolderExpanded(id, expanded, context) {
        if (context) {
            return this.syncBookmarkUpdate(context.version, context.item, { expanded });
        }
        return this.updateBookmark(id, { expanded });
    }
    /**
     * Move a bookmark to a different folder.
     * @param id Bookmark ID.
     * @param newParentId New Parent Folder ID.
     */
    async moveBookmark(id, newParentId) {
        return this.updateBookmark(id, { parentId: newParentId });
    }
}
exports.BookmarkService = BookmarkService;
